/**
 * Lightweight client-side persistence for assessments completed via the
 * intake wizard.  Writes to localStorage; future iteration will swap this
 * for a real /api/assessments endpoint without changing the call sites.
 */

const KEY = "riskaia.assessments.v1";

const TOEPASSING_LABELS = {
  sociaal: "Sociaal Domein",
  handhaving: "Handhaving & Openbare Ruimte",
  hr: "HR & Werving",
  zorg: "Gezondheidszorg",
  informatie: "Informatiebeheer",
};

const INZET_LABELS = {
  auto: "Volledig geautomatiseerde besluitvorming",
  support: "Besluitondersteuning voor ambtenaren",
  process: "Interne procesoptimalisatie",
};

const DATA_LABELS = {
  personal: "Persoonsgegevens",
  special: "Bijzondere persoonsgegevens",
  biometric: "Biometrische data",
  public: "Publieke data",
};

const REGION_LABELS = {
  nl: "Nederland",
  eu: "Europese Unie",
  world: "Wereldwijd",
};

const SAFEGUARD_LABELS = {
  fg: "FG / DPO aangesteld",
  policy: "AI-beleid vastgesteld",
  monitoring: "Continue monitoring op bias",
  dpia: "DPIA uitgevoerd",
};

// ---------- Risk level ----------
export function computeRisk(form) {
  const hasSpecial =
    form.dataTypes.includes("special") || form.dataTypes.includes("biometric");
  const isAuto = form.inzet === "auto";
  const sensitiveDomain =
    form.toepassing === "sociaal" || form.toepassing === "zorg";

  if (hasSpecial && (isAuto || sensitiveDomain)) return "high";
  if (isAuto || hasSpecial || sensitiveDomain) return "medium";
  if (form.dataTypes.length === 0 || form.dataTypes.every((d) => d === "public"))
    return "low";
  return "medium";
}

export const RISK_LABEL = {
  high: "Hoog risico",
  medium: "Midden risico",
  low: "Laag risico",
};

// ---------- VNG step readiness ----------
// Returns map: { initiatie: true, "data-analyse": false, ... }
export function computeVngStatus(form) {
  return {
    initiatie:
      !!form.orgName.trim() &&
      !!form.systemName.trim() &&
      !!form.sector,
    "data-analyse": form.dataTypes.length > 0,
    ontwerp: !!form.toepassing,
    ontwikkeling: !!form.size && form.size !== "1-10",
    validatie:
      form.safeguards.includes("dpia") ||
      form.safeguards.includes("monitoring"),
    implementatie: !!form.inzet,
    monitoring: form.safeguards.includes("monitoring"),
    evaluatie: form.safeguards.includes("policy"),
  };
}

// ---------- Narrative ----------
export function generateNarrative(form) {
  const domain = TOEPASSING_LABELS[form.toepassing] || "het sociale domein";
  const inzet = (INZET_LABELS[form.inzet] || "ondersteunende inzet").toLowerCase();
  const dataPhrase =
    form.dataTypes.length === 0
      ? "publieke data"
      : form.dataTypes.map((d) => DATA_LABELS[d]).join(", ").toLowerCase();
  const regionPhrase =
    form.regions.length > 0
      ? form.regions.map((r) => REGION_LABELS[r]).join(", ")
      : "Nederland";

  return `${form.systemName} is een AI-systeem van ${form.orgName} binnen het domein ${domain}. Het wordt ingezet als ${inzet} en verwerkt ${dataPhrase}. Het systeem opereert in ${regionPhrase} en valt daarmee onder het VNG-kader voor algoritme­governance. RiskaiΛ bewaakt de lifecycle continu en signaleert direct wanneer een waarborg wegvalt of het risico verandert.`;
}

// ---------- Persistence ----------
export function loadAssessments() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function saveAssessment(form, extra = {}) {
  const id = `usr-${Date.now().toString(36)}`;
  const code = `LWD-U${(loadAssessments().length + 1)
    .toString()
    .padStart(2, "0")}`;
  const risk = computeRisk(form);
  const vngStatus = computeVngStatus(form);
  const narrative = generateNarrative(form);
  const safeguardLabels = form.safeguards.map((s) => SAFEGUARD_LABELS[s]);

  const record = {
    id,
    code,
    name: form.systemName.trim() || "Nieuw AI-systeem",
    org: form.orgName,
    sector: form.sector,
    size: form.size,
    domain: TOEPASSING_LABELS[form.toepassing] || "Algemeen",
    domainKey: form.toepassing,
    inzet: INZET_LABELS[form.inzet] || "Onbepaald",
    inzetKey: form.inzet,
    regions: form.regions,
    dataTypes: form.dataTypes,
    safeguards: form.safeguards,
    safeguardLabels,
    risk,
    riskLabel: RISK_LABEL[risk],
    vngStatus,
    narrative,
    createdAt: new Date().toISOString(),
    published: false,
    publishedAt: null,
    // `backendId` links this client-side dossier to the server-persisted
    // assessment (riskaia-backend), which owns the canonical compliance
    // report PDF. Empty when the backend was unreachable at submit time.
    ...extra,
  };

  const all = loadAssessments();
  all.push(record);
  try {
    localStorage.setItem(KEY, JSON.stringify(all));
  } catch {
    /* storage full / disabled — fail silently */
  }
  return record;
}

export function findAssessment(id) {
  return loadAssessments().find((a) => a.id === id) || null;
}

export function publishAssessment(id) {
  const all = loadAssessments();
  const idx = all.findIndex((a) => a.id === id);
  if (idx === -1) return null;
  all[idx] = {
    ...all[idx],
    published: true,
    publishedAt: new Date().toISOString(),
  };
  try {
    localStorage.setItem(KEY, JSON.stringify(all));
  } catch {
    /* ignore */
  }
  return all[idx];
}

export function deleteAssessment(id) {
  const all = loadAssessments();
  const next = all.filter((a) => a.id !== id);
  try {
    localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    /* ignore */
  }
  return next;
}

// ---------------- Per-step toggle ----------------
//
// For user assessments we mutate the record's vngStatus directly.
// For Leeuwarden seeds we keep a separate "overrides" map in localStorage so
// toggles persist across refreshes without touching the seed source files.

const SEED_OVERRIDES_KEY = "riskaia.seedVngOverrides.v1";

export function loadSeedOverrides() {
  try {
    const raw = localStorage.getItem(SEED_OVERRIDES_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function saveSeedOverrides(map) {
  try {
    localStorage.setItem(SEED_OVERRIDES_KEY, JSON.stringify(map));
  } catch {
    /* ignore */
  }
}

export function setVngStep(systemId, stepId, value, isSeed = false, stepLabel) {
  if (isSeed) {
    const map = loadSeedOverrides();
    map[systemId] = { ...(map[systemId] || {}), [stepId]: value };
    saveSeedOverrides(map);
    addLogEntry(
      systemId,
      "vng-toggle",
      `Stap ${stepLabel || stepId} gemarkeerd als ${
        value ? "voltooid" : "incompleet"
      }`,
      true
    );
    return value;
  }
  const all = loadAssessments();
  const idx = all.findIndex((a) => a.id === systemId);
  if (idx === -1) return null;
  const nextStatus = { ...(all[idx].vngStatus || {}), [stepId]: value };
  all[idx] = { ...all[idx], vngStatus: nextStatus };
  try {
    localStorage.setItem(KEY, JSON.stringify(all));
  } catch {
    /* ignore */
  }
  addLogEntry(
    systemId,
    "vng-toggle",
    `Stap ${stepLabel || stepId} gemarkeerd als ${
      value ? "voltooid" : "incompleet"
    }`,
    false
  );
  return value;
}

// ---------------- Update / version-control ----------------
//
// Re-assessments: a user steps through the wizard for an *existing* system
// (seed or user-created). We persist:
//   - the new form data (so the next edit pre-fills correctly)
//   - the chosen "Versiebeheer" reason
//   - a lastUpdated timestamp
//   - an append-only history of versions
//
// For seeds we keep this in a dedicated map so the original seed source files
// stay untouched. For user assessments we mutate the record directly.

const SEED_UPDATES_KEY = "riskaia.seedUpdates.v1";

// Form profiles for the four Leeuwarden seeds — used to pre-fill the wizard
// when the user opens "Bestaand AI-systeem updaten".
export const LWD_FORM_PROFILES = {
  uitkeringscheck: {
    orgName: "Gemeente Leeuwarden",
    systemName: "Uitkeringscheck",
    size: "1001-10000",
    sector: "gemeente",
    regions: ["nl"],
    toepassing: "sociaal",
    dataTypes: ["personal", "special"],
    inzet: "support",
    safeguards: ["fg", "monitoring", "policy"],
  },
  schuldhulpverlening: {
    orgName: "Gemeente Leeuwarden",
    systemName: "Vroegsignalering Schulden",
    size: "1001-10000",
    sector: "gemeente",
    regions: ["nl"],
    toepassing: "sociaal",
    dataTypes: ["personal", "special"],
    inzet: "support",
    safeguards: ["fg", "monitoring"],
  },
  "wmo-zorg": {
    orgName: "Gemeente Leeuwarden",
    systemName: "WMO Zorgtoewijzing",
    size: "1001-10000",
    sector: "gemeente",
    regions: ["nl"],
    toepassing: "zorg",
    dataTypes: ["personal", "special"],
    inzet: "support",
    safeguards: ["fg", "dpia", "policy"],
  },
  handhaving: {
    orgName: "Gemeente Leeuwarden",
    systemName: "Handhaving Openbare Ruimte",
    size: "1001-10000",
    sector: "gemeente",
    regions: ["nl"],
    toepassing: "handhaving",
    dataTypes: ["public"],
    inzet: "process",
    safeguards: ["fg", "policy"],
  },
};

export function loadSeedUpdates() {
  try {
    const raw = localStorage.getItem(SEED_UPDATES_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function saveSeedUpdates(map) {
  try {
    localStorage.setItem(SEED_UPDATES_KEY, JSON.stringify(map));
  } catch {
    /* ignore */
  }
}

// Build a pre-fill form snapshot for the wizard, given a system id.
// Returns { form, isSeed, displayName } or null when not found.
export function loadSystemForEdit(id) {
  // 1) User assessment
  const u = findAssessment(id);
  if (u) {
    return {
      isSeed: false,
      displayName: u.name,
      form: {
        orgName: u.org || "",
        systemName: u.name || "",
        size: u.size || "1001-10000",
        sector: u.sector || "gemeente",
        regions: u.regions || ["nl"],
        toepassing: u.domainKey || "",
        dataTypes: u.dataTypes || [],
        inzet: u.inzetKey || "",
        safeguards: u.safeguards || [],
      },
    };
  }
  // 2) Seed system — start from the LWD_FORM_PROFILES, then layer any
  //    previously saved seed-update form data.
  const seedProfile = LWD_FORM_PROFILES[id];
  if (!seedProfile) return null;
  const seedUpdates = loadSeedUpdates();
  const lastForm = seedUpdates[id]?.form;
  return {
    isSeed: true,
    displayName: seedProfile.systemName,
    form: { ...seedProfile, ...(lastForm || {}) },
  };
}

// Persist the result of a re-assessment.
//   - For user assessments: mutate the record in place
//   - For seeds: write into seedUpdates map (and also recompute vngStatus
//     into seedOverrides so the gauges/score reflect the new answers)
export function applySystemUpdate(id, form, versionNote, isSeed = false) {
  const now = new Date().toISOString();
  const vngStatus = computeVngStatus(form);
  const risk = computeRisk(form);

  if (isSeed) {
    const map = loadSeedUpdates();
    const previous = map[id] || { history: [] };
    map[id] = {
      form,
      versionNote,
      lastUpdated: now,
      risk,
      history: [
        ...(previous.history || []),
        {
          versionNote: previous.versionNote || versionNote,
          updatedAt: previous.lastUpdated || now,
        },
      ].filter((h) => h.versionNote),
    };
    saveSeedUpdates(map);
    // Also write the recomputed vng status into seedOverrides so SystemResult
    // surfaces the new gauges immediately.
    const overrides = loadSeedOverrides();
    overrides[id] = { ...vngStatus };
    saveSeedOverrides(overrides);
    return { ...map[id] };
  }

  const all = loadAssessments();
  const idx = all.findIndex((a) => a.id === id);
  if (idx === -1) return null;
  const previous = all[idx];
  all[idx] = {
    ...previous,
    name: form.systemName.trim() || previous.name,
    org: form.orgName,
    sector: form.sector,
    size: form.size,
    regions: form.regions,
    dataTypes: form.dataTypes,
    domainKey: form.toepassing,
    inzetKey: form.inzet,
    safeguards: form.safeguards,
    risk,
    riskLabel: RISK_LABEL[risk],
    vngStatus,
    versionNote,
    lastUpdated: now,
    history: [
      ...(previous.history || []),
      {
        versionNote: previous.versionNote || versionNote,
        updatedAt: previous.lastUpdated || previous.createdAt || now,
      },
    ].filter((h) => h.versionNote),
  };
  try {
    localStorage.setItem(KEY, JSON.stringify(all));
  } catch {
    /* ignore */
  }
  return all[idx];
}

// ====================================================================
// Phase 1 — Evidence dossier & project roles
// ====================================================================
//
// `seedEvidence`  : { [seedId]: { [stepId]: { note: "...", document: "filename.pdf" } } }
// `seedRoles`     : { [seedId]: { projectleider: bool, fgDpo: bool, ethicus: bool, auditor: bool } }
// For user assessments these maps are stored on the record itself.

const SEED_EVIDENCE_KEY = "riskaia.seedEvidence.v1";
const SEED_ROLES_KEY = "riskaia.seedRoles.v1";
// Append-only mutation log per system — stored separately from the seed
// source files so we can ship a verifiable audit trail without ever
// touching the canonical Leeuwarden data.
// Shape: { [systemId]: [{ timestamp, action, detail }] }
const SEED_LOGS_KEY = "riskaia.seedMutationLogs.v1";

// Cap the per-system log at this size to keep localStorage healthy. The
// most recent entries are kept; older ones are dropped silently. 500 covers
// years of normal Cockpit use.
const MAX_LOG_ENTRIES_PER_SYSTEM = 500;

export const ROLE_KEYS = ["projectleider", "fgDpo", "ethicus", "auditor"];

export const ROLE_LABELS = {
  projectleider: "Projectleider",
  fgDpo: "FG / DPO",
  ethicus: "Ethicus",
  auditor: "Auditor",
};

const EMPTY_ROLES = () => ({
  projectleider: { active: false, name: "" },
  fgDpo: { active: false, name: "" },
  ethicus: { active: false, name: "" },
  auditor: { active: false, name: "" },
});

// Legacy data may store role values as plain booleans (`true`/`false`).
// Normalize ANY shape into the canonical `{active, name}` envelope so the
// rest of the platform can keep using `roles.fgDpo.active` and
// `roles.fgDpo.name` without runtime guards.
function normalizeRole(value) {
  if (value && typeof value === "object") {
    return {
      active: !!value.active,
      name: typeof value.name === "string" ? value.name : "",
    };
  }
  return { active: !!value, name: "" };
}

function normalizeRoles(rolesObj) {
  const out = EMPTY_ROLES();
  if (!rolesObj || typeof rolesObj !== "object") return out;
  Object.keys(out).forEach((k) => {
    out[k] = normalizeRole(rolesObj[k]);
  });
  return out;
}

// Convenience boolean accessor — used by the readiness scorer and any
// consumer that only cares "is this role assigned?".
export function isRoleActive(roles, key) {
  if (!roles || !roles[key]) return false;
  const v = roles[key];
  return typeof v === "object" ? !!v.active : !!v;
}

function readJSON(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writeJSON(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* ignore */
  }
}

// ----- Evidence -----
export function loadSeedEvidence() {
  return readJSON(SEED_EVIDENCE_KEY);
}

export function setStepEvidence(systemId, stepId, evidence, isSeed = false, stepLabel) {
  const next = {
    note: (evidence && evidence.note) || "",
    document: (evidence && evidence.document) || "",
  };
  // Read previous evidence so we can produce a meaningful audit-log diff
  // ("Onderbouwing bijgewerkt" vs "Document gekoppeld").
  const prevMap = isSeed
    ? loadSeedEvidence()
    : ((loadAssessments().find((a) => a.id === systemId) || {}).evidence ||
      {});
  const prevSeedEntry = isSeed
    ? (prevMap[systemId] || {})[stepId] || { note: "", document: "" }
    : prevMap[stepId] || { note: "", document: "" };

  if (isSeed) {
    const map = loadSeedEvidence();
    map[systemId] = {
      ...(map[systemId] || {}),
      [stepId]: next,
    };
    writeJSON(SEED_EVIDENCE_KEY, map);
  } else {
    const all = loadAssessments();
    const idx = all.findIndex((a) => a.id === systemId);
    if (idx === -1) return null;
    const prevEvidence = all[idx].evidence || {};
    all[idx] = {
      ...all[idx],
      evidence: { ...prevEvidence, [stepId]: next },
    };
    try {
      localStorage.setItem(KEY, JSON.stringify(all));
    } catch {
      /* ignore */
    }
  }

  // Emit audit-log entries — at most one per kind of change, in order.
  const labelForStep = stepLabel || stepId;
  if ((prevSeedEntry.document || "") !== next.document) {
    if (next.document) {
      addLogEntry(
        systemId,
        "evidence-document",
        `Document "${next.document}" gekoppeld aan Stap ${labelForStep}`,
        isSeed
      );
    } else {
      addLogEntry(
        systemId,
        "evidence-document",
        `Document verwijderd voor Stap ${labelForStep}`,
        isSeed
      );
    }
  }
  if ((prevSeedEntry.note || "") !== next.note) {
    addLogEntry(
      systemId,
      "evidence-note",
      next.note
        ? `Onderbouwing bijgewerkt voor Stap ${labelForStep}`
        : `Onderbouwing leeggemaakt voor Stap ${labelForStep}`,
      isSeed
    );
  }

  return next;
}

// Returns a fully-populated evidence map for a system, with empty defaults
// for any missing step.
export function getEvidenceMap(system) {
  const out = {};
  const src = system.isSeed
    ? loadSeedEvidence()[system.id] || {}
    : system.evidence || {};
  // Don't import vngSteps here to avoid a cycle — caller passes step ids.
  Object.keys(src).forEach((k) => {
    out[k] = {
      note: src[k]?.note || "",
      document: src[k]?.document || "",
    };
  });
  return out;
}

// ----- Roles -----
export function loadSeedRoles() {
  return readJSON(SEED_ROLES_KEY);
}

export function setSystemRoles(systemId, roles, isSeed = false) {
  const next = normalizeRoles(roles);
  // Capture the previous role-map so we can produce per-role audit entries.
  const prev = isSeed
    ? normalizeRoles(loadSeedRoles()[systemId])
    : normalizeRoles(
        ((loadAssessments().find((a) => a.id === systemId)) || {}).roles
      );

  if (isSeed) {
    const map = loadSeedRoles();
    map[systemId] = next;
    writeJSON(SEED_ROLES_KEY, map);
  } else {
    const all = loadAssessments();
    const idx = all.findIndex((a) => a.id === systemId);
    if (idx === -1) return null;
    all[idx] = { ...all[idx], roles: next };
    try {
      localStorage.setItem(KEY, JSON.stringify(all));
    } catch {
      /* ignore */
    }
  }

  // Per-role audit entries (only for actual changes).
  ROLE_KEYS.forEach((key) => {
    const label = ROLE_LABELS[key] || key;
    const before = prev[key] || { active: false, name: "" };
    const after = next[key] || { active: false, name: "" };

    // Active-state toggle
    if (!!before.active !== !!after.active) {
      addLogEntry(
        systemId,
        "role-toggle",
        `Projectrol ${label} ${after.active ? "toegewezen" : "verwijderd"}`,
        isSeed
      );
    }
    // Name change (only meaningful when role remains active; on
    // deactivation the toggle log already covers the removal).
    if (after.active && (before.name || "") !== (after.name || "")) {
      const detail = after.name
        ? `Naam voor rol ${label} vastgelegd: ${after.name}`
        : `Naam voor rol ${label} verwijderd`;
      addLogEntry(systemId, "role-name", detail, isSeed);
    }
  });

  return next;
}

export function getSystemRoles(system) {
  const fromSeed = system.isSeed
    ? loadSeedRoles()[system.id] || null
    : null;
  return normalizeRoles(fromSeed || system.roles);
}

// ----- Scoring helpers -----
//
// Tiered scoring: a step is "gold" (full weight) only when checkbox AND
// either a note or a document is present. A bare checkbox without
// supporting evidence counts as "silver" (half-weight). No checkbox =
// "open" (zero).
export function scoreStep(checked, evidenceForStep) {
  if (!checked) return { tier: "open", weight: 0 };
  const hasEvidence = !!(
    evidenceForStep &&
    ((evidenceForStep.note && evidenceForStep.note.trim().length > 0) ||
      evidenceForStep.document)
  );
  if (hasEvidence) return { tier: "gold", weight: 1 };
  return { tier: "silver", weight: 0.5 };
}

// Compute the readiness % for a system, applying the FG/DPO cap on
// high-risk systems (cannot exceed 95% if FG/DPO role is unassigned).
export function computeReadiness({
  vngStatus,
  evidence,
  vngStepIds,
  risk,
  roles,
}) {
  const total = vngStepIds.length;
  let score = 0;
  let goldCount = 0;
  vngStepIds.forEach((id) => {
    const s = scoreStep(!!vngStatus[id], evidence[id]);
    score += s.weight;
    if (s.tier === "gold") goldCount += 1;
  });
  let pct = Math.round((score / total) * 100);
  // High-risk cap: missing FG/DPO blocks readiness above 95%.
  const fgMissing = !isRoleActive(roles, "fgDpo");
  const capped = risk === "high" && fgMissing && pct > 95;
  if (capped) pct = 95;
  // Dossier completeness = % of checked steps with full evidence
  const checkedCount = vngStepIds.filter((id) => !!vngStatus[id]).length;
  const dossierPct =
    checkedCount === 0
      ? 0
      : Math.round((goldCount / checkedCount) * 100);
  return { pct, goldCount, dossierPct, capped };
}


// ====================================================================
// Mutation Log — audit trail per system (EU AI Act Art. 12 traceability)
// ====================================================================
//
// Every administrative action inside the Cockpit (toggling a VNG step,
// editing a note, uploading a document, flipping a project role) is
// appended to this immutable log. The log is the audit-proof spine of
// the platform: regulators, auditors and gemeenteraad members can
// reconstruct *what happened, when, and to which artefact*.

export function loadSeedMutationLogs() {
  return readJSON(SEED_LOGS_KEY);
}

function saveSeedMutationLogs(map) {
  writeJSON(SEED_LOGS_KEY, map);
}

// Append an entry. Caller decides whether the host is a seed system or a
// user assessment (mirrors the existing `isSeed` flag pattern).
export function addLogEntry(systemId, action, detail, isSeed = false) {
  const entry = {
    timestamp: new Date().toISOString(),
    action,
    detail,
  };
  if (isSeed) {
    const map = loadSeedMutationLogs();
    const current = Array.isArray(map[systemId]) ? map[systemId] : [];
    const next = [...current, entry].slice(-MAX_LOG_ENTRIES_PER_SYSTEM);
    map[systemId] = next;
    saveSeedMutationLogs(map);
    return entry;
  }
  const all = loadAssessments();
  const idx = all.findIndex((a) => a.id === systemId);
  if (idx === -1) return null;
  const current = Array.isArray(all[idx].mutationLogs)
    ? all[idx].mutationLogs
    : [];
  const next = [...current, entry].slice(-MAX_LOG_ENTRIES_PER_SYSTEM);
  all[idx] = { ...all[idx], mutationLogs: next };
  try {
    localStorage.setItem(KEY, JSON.stringify(all));
  } catch {
    /* ignore */
  }
  return entry;
}

// Returns the most recent list of log entries for the given system,
// always reading from durable storage so it is never stale. The UI is
// responsible for reversing the order when rendering.
export function getMutationLogs(system) {
  if (!system) return [];
  if (system.isSeed) {
    return loadSeedMutationLogs()[system.id] || [];
  }
  const fresh = loadAssessments().find((a) => a.id === system.id);
  if (fresh && Array.isArray(fresh.mutationLogs)) return fresh.mutationLogs;
  return Array.isArray(system.mutationLogs) ? system.mutationLogs : [];
}

